import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export default function TemplatePreview({ blocks }) {
  const [showPreview, setShowPreview] = useState(true);
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    // Generate HTML from blocks
    const blockStructure = {
      blocks,
      styles: {
        backgroundColor: '#f3f4f6',
        fontFamily: 'Arial, sans-serif',
      },
    };

    // Simple client-side HTML generation (for preview)
    // In production, this would call the backend service
    const html = generatePreviewHTML(blockStructure);
    setHtmlContent(html);
  }, [blocks]);

  const generatePreviewHTML = (blockStructure) => {
    if (!blockStructure.blocks || blockStructure.blocks.length === 0) {
      return '<p style="padding: 20px; color: #9ca3af;">No content yet</p>';
    }

    let html = '<div style="max-width: 650px; margin: 0 auto; background: white; padding: 32px;">';

    blockStructure.blocks.forEach((block) => {
      const { type, content, props = {} } = block;
      const safeContent = escapeHtml(content || '');

      switch (type) {
        case 'header':
          const level = props.level || 1;
          const fontSize = level === 1 ? '32px' : level === 2 ? '24px' : '20px';
          html += `<h${level} style="font-size: ${fontSize}; font-weight: bold; color: #1E5BB5; margin: 16px 0;">${safeContent}</h${level}>`;
          break;

        case 'text':
          html += `<p style="font-size: 16px; line-height: 1.7; color: #1F2937; margin: 12px 0;">${safeContent}</p>`;
          break;

        case 'button':
          const bgColor = props.color === 'blue' ? '#1E5BB5' : props.color === 'green' ? '#00B359' : '#FFA726';
          html += `<div style="margin: 20px 0; text-align: ${props.align || 'center'}">
            <a href="${props.url || '#'}" style="display: inline-block; padding: 12px 24px; background-color: ${bgColor}; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: bold;">${safeContent}</a>
          </div>`;
          break;

        case 'image':
          if (props.src) {
            html += `<div style="margin: 20px 0; text-align: ${props.align || 'center'}">
              <img src="${props.src}" alt="${safeContent}" style="max-width: 100%; height: auto; border-radius: 4px;" />
            </div>`;
          } else {
            html += `<div style="border: 2px dashed #d1d5db; border-radius: 4px; padding: 40px; text-align: center; color: #9ca3af; margin: 20px 0;">Image placeholder</div>`;
          }
          break;

        case 'divider':
          html += '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;" />';
          break;

        case 'spacer':
          html += `<div style="height: ${props.height || 20}px;"></div>`;
          break;

        default:
          html += `<p style="margin: 12px 0;">${safeContent}</p>`;
      }
    });

    html += '</div>';
    return html;
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  return (
    <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Preview</h3>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="p-1 text-gray-400 hover:text-gray-600"
          title={showPreview ? 'Hide preview' : 'Show preview'}
        >
          {showPreview ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {showPreview && (
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div
              className="preview-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{ minHeight: '400px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

